import { useCallback } from 'react';
import {
  ChartData,
  GameState,
  LimitOrder,
  Trade,
} from '../../../shared/types/game.types';
import { formatPrice } from '../../../shared/utils/format';
import { adjustToTickSize } from '../../../shared/utils/tickSize';
import {
  calculateBuyCost,
  calculateSellRevenue,
  BROKERAGE_FEE_RATE,
  TRANSACTION_TAX_RATE,
} from '../../../shared/utils/tradingFees';
import { generateFeedback } from '../utils/feedbackGenerator';

interface UseTradingProps {
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  quantity: string;
  setQuantity: (quantity: string) => void;
  limitOrderPrice: string;
  setLimitOrderPrice: (price: string) => void;
  isMarketOrder: boolean;
  tradeHistory: Trade[];
  setTradeHistory: React.Dispatch<React.SetStateAction<Trade[]>>;
  notify: (
    message: string,
    type?: 'positive' | 'negative' | 'neutral' | 'tip',
    durationMs?: number
  ) => void;
}

/**
 * 거래 관리 훅
 */
export const useTrading = ({
  gameState,
  setGameState,
  quantity,
  setQuantity,
  limitOrderPrice,
  setLimitOrderPrice,
  isMarketOrder,
  tradeHistory,
  setTradeHistory,
  notify,
}: UseTradingProps) => {
  const createLimitOrder = useCallback(
    (type: 'buy' | 'sell', price: number, quantityValue: number) => {
      // 매수 주문 시 잔고 체크 (수수료 포함)
      if (type === 'buy') {
        const totalCost = calculateBuyCost(price, quantityValue);
        if (totalCost > gameState.playerBalance) {
          notify(
            `보유 현금이 부족합니다\n필요: ${formatPrice(
              totalCost
            )} / 보유: ${formatPrice(gameState.playerBalance)}`,
            'negative'
          );
          return;
        }
      }

      // 매도 주문 시 보유 주식 체크
      if (type === 'sell') {
        if (quantityValue > gameState.stockQuantity) {
          notify(
            `보유 주식이 부족합니다\n요청: ${quantityValue.toLocaleString()}주 / 보유: ${gameState.stockQuantity.toLocaleString()}주`,
            'negative'
          );
          return;
        }
      }

      const order: LimitOrder = {
        id: Date.now().toString(),
        type,
        price,
        size: quantityValue,
        time: new Date(),
        status: 'pending',
      };

      setGameState(prev => {
        // 지정가 매수 주문 생성 시: 보유현금 차감 (예약금, 수수료 포함)
        // 총자산은 변하지 않아야 함 (차감한 금액은 "예약된 자산"으로 간주)
        let newBalance = prev.playerBalance;

        if (type === 'buy') {
          const totalCost = calculateBuyCost(price, quantityValue);
          if (prev.playerBalance >= totalCost) {
            newBalance -= totalCost; // 보유현금에서 차감 (수수료 포함)
          } else {
            // 이론적으로 여기 도달하면 안 됨 (위에서 이미 체크함)
            alert(
              `보유 현금이 부족합니다.\n필요 금액: ${formatPrice(
                totalCost
              )} (수수료 포함)\n보유 현금: ${formatPrice(prev.playerBalance)}`
            );
            return prev;
          }
        }

        return {
          ...prev,
          limitOrders: [...prev.limitOrders, order],
          playerBalance: newBalance, // 보유현금 차감 (총자산 계산 시 예약금 포함)
        };
      });

      const feedback = generateFeedback({
        action: type === 'buy' ? '매수' : '매도',
        price,
        quantityValue,
        currentPrice: gameState.currentPrice,
      });

      setGameState(prev => ({
        ...prev,
        feedback: {
          ...feedback,
          message: `${
            type === 'buy' ? '매수' : '매도'
          } 지정가 주문이 등록되었습니다. (${formatPrice(
            price
          )} × ${quantityValue.toLocaleString()}주)`,
        },
      }));

      notify(
        `${type === 'buy' ? '매수' : '매도'} 지정가 주문 등록\n${formatPrice(
          price
        )} × ${quantityValue.toLocaleString()}주`,
        type === 'buy' ? 'positive' : 'negative'
      );
    },
    [
      gameState.currentPrice,
      gameState.playerBalance,
      gameState.stockQuantity,
      setGameState,
      formatPrice,
      notify,
    ]
  );

  const cancelLimitOrder = useCallback(
    (orderId: string) => {
      setGameState(prev => {
        const orderToCancel = prev.limitOrders.find(
          order => order.id === orderId
        );
        if (!orderToCancel || orderToCancel.status !== 'pending') {
          return prev;
        }

        // 지정가 주문 취소 시: 보유현금 반환 (예약금 해제)
        let newBalance = prev.playerBalance;

        if (orderToCancel.type === 'buy') {
          const totalCost = calculateBuyCost(
            orderToCancel.price,
            orderToCancel.size
          );
          newBalance += totalCost; // 보유현금 반환 (수수료 포함된 금액)
        }

        return {
          ...prev,
          limitOrders: prev.limitOrders.map(order =>
            order.id === orderId ? { ...order, status: 'cancelled' } : order
          ),
          playerBalance: newBalance, // 보유현금 반환
        };
      });
    },
    [setGameState]
  );

  const buy = useCallback(() => {
    if (!gameState.isGameActive) return;

    const quantityNum = Number(quantity);
    if (!quantity || quantityNum <= 0 || isNaN(quantityNum)) {
      notify('수량을 입력해주세요.', 'negative');
      return;
    }

    // 시장가 또는 지정가 가격으로 총 비용 계산 (매수 수수료만 포함)
    const orderPrice = isMarketOrder
      ? gameState.currentPrice
      : adjustToTickSize(Number(limitOrderPrice) || gameState.currentPrice);

    const buyCost = calculateBuyCost(orderPrice, quantityNum);

    // 보유 현금 체크 (매수 수수료만 포함)
    if (buyCost > gameState.playerBalance) {
      // 매수 수수료를 고려한 최대 매수 가능 수량 계산
      // buyCost = price * qty * (1 + 0.00015)
      // qty = playerBalance / (price * 1.00015)
      const baseAmount = gameState.playerBalance / (1 + BROKERAGE_FEE_RATE);
      const maxAffordableQuantity = Math.floor(baseAmount / orderPrice);
      notify(
        `보유 현금이 부족합니다\n최대 매수 가능: ${maxAffordableQuantity.toLocaleString()}주`,
        'negative',
        2600
      );
      // 최대 매수 가능 수량으로 자동 조정
      if (maxAffordableQuantity > 0) {
        setQuantity(maxAffordableQuantity.toString());
      }
      return;
    }

    if (isMarketOrder) {
      // 실제 체결 가격은 시장가 (gameState.currentPrice)
      const actualPrice = gameState.currentPrice;
      const actualTotalCost = calculateBuyCost(actualPrice, quantityNum);

      // 잔고 재확인 (실제 체결 가격 기준, 수수료 포함)
      if (actualTotalCost > gameState.playerBalance) {
        notify('보유 현금이 부족합니다.', 'negative');
        return;
      }

      // 현재 캔들의 날짜 가져오기 (거래가 발생한 날짜)
      const currentCandle =
        gameState.history.length > 0
          ? gameState.history[gameState.history.length - 1]
          : null;
      const tradeDate = currentCandle
        ? new Date(currentCandle.timestamp)
        : new Date();

      // 거래 내역 추가
      setTradeHistory(prev => [
        {
          type: '매수',
          price: actualPrice,
          size: quantityNum,
          time: tradeDate,
          profit: 0,
        },
        ...prev,
      ]);

      // 피드백 생성
      const feedback = generateFeedback({
        action: '매수',
        price: actualPrice,
        quantityValue: quantityNum,
        currentPrice: actualPrice,
      });

      // 상태 업데이트 (한 번에 처리)
      setGameState(prev => {
        // 잔고 최종 확인 (수수료 포함)
        if (prev.playerBalance < actualTotalCost) {
          console.error('잔고 부족: 매수 취소', {
            playerBalance: prev.playerBalance,
            totalCost: actualTotalCost,
          });
          return prev;
        }

        const newStockQuantity = prev.stockQuantity + quantityNum;
        const newAveragePrice =
          newStockQuantity > 0
            ? Math.floor(
                (prev.averagePrice * prev.stockQuantity +
                  actualPrice * quantityNum) /
                  newStockQuantity
              )
            : actualPrice;

        const newPlayerBalance = prev.playerBalance - actualTotalCost;

        console.log('매수 처리:', {
          이전잔고: prev.playerBalance,
          매수금액: actualTotalCost,
          새로운잔고: newPlayerBalance,
          수량: quantityNum,
          가격: actualPrice,
          수수료포함: true,
        });

        return {
          ...prev,
          playerBalance: newPlayerBalance,
          stockQuantity: newStockQuantity,
          averagePrice: newAveragePrice,
          feedback,
        };
      });

      notify(
        `매수 완료\n${formatPrice(actualPrice)} × ${quantityNum.toLocaleString()}주`,
        'positive'
      );
    } else {
      // 지정가 매수는 createLimitOrder에서 체크하므로 여기서는 호출만
      const orderPrice = adjustToTickSize(
        Number(limitOrderPrice) || gameState.currentPrice
      );

      createLimitOrder('buy', orderPrice, quantityNum);
    }
  }, [
    gameState,
    quantity,
    isMarketOrder,
    limitOrderPrice,
    setGameState,
    setTradeHistory,
    createLimitOrder,
    notify,
  ]);

  const sell = useCallback(() => {
    if (!gameState.isGameActive) return;

    const quantityNum = Number(quantity);
    if (!quantity || quantityNum <= 0) {
      notify('수량을 입력해주세요.', 'negative');
      return;
    }

    if (quantityNum > gameState.stockQuantity) {
      notify(
        `보유 주식이 부족합니다\n요청: ${quantityNum.toLocaleString()}주 / 보유: ${gameState.stockQuantity.toLocaleString()}주`,
        'negative'
      );
      return;
    }

    const actualPrice = gameState.currentPrice;
    const actualRevenue = calculateSellRevenue(actualPrice, quantityNum);

    if (isMarketOrder) {
      // 현재 캔들의 날짜 가져오기 (거래가 발생한 날짜)
      const currentCandle =
        gameState.history.length > 0
          ? gameState.history[gameState.history.length - 1]
          : null;
      const tradeDate = currentCandle
        ? new Date(currentCandle.timestamp)
        : new Date();

      setTradeHistory(prev => [
        {
          type: '매도',
          price: actualPrice,
          size: quantityNum,
          time: tradeDate,
          profit: 0,
        },
        ...prev,
      ]);

      setGameState(prev => {
        const newStockQuantity = prev.stockQuantity - quantityNum;
        const newAveragePrice = newStockQuantity > 0 ? prev.averagePrice : 0;

        console.log('매도 처리:', {
          이전잔고: prev.playerBalance,
          매도금액: actualPrice * quantityNum,
          수수료및거래세: actualPrice * quantityNum - actualRevenue,
          실제수익: actualRevenue,
          새로운잔고: prev.playerBalance + actualRevenue,
          수량: quantityNum,
          가격: actualPrice,
        });

        return {
          ...prev,
          playerBalance: prev.playerBalance + actualRevenue, // 수수료와 거래세 차감된 금액
          stockQuantity: newStockQuantity,
          averagePrice: newAveragePrice,
        };
      });

      const feedback = generateFeedback({
        action: '매도',
        price: actualPrice,
        quantityValue: quantityNum,
        currentPrice: actualPrice,
      });
      setGameState(prev => ({
        ...prev,
        feedback,
      }));

      notify(
        `매도 완료\n${formatPrice(actualPrice)} × ${quantityNum.toLocaleString()}주`,
        'negative'
      );
    } else {
      const orderPrice = adjustToTickSize(
        Number(limitOrderPrice) || gameState.currentPrice
      );
      createLimitOrder('sell', orderPrice, quantityNum);
    }
  }, [
    gameState,
    quantity,
    isMarketOrder,
    limitOrderPrice,
    setGameState,
    setTradeHistory,
    createLimitOrder,
    notify,
  ]);

  const calculateBuyPercentage = useCallback(
    (percentage: number) => {
      if (!gameState.isGameActive || gameState.playerBalance <= 0) return;

      // 사용 가능한 금액 계산 (잔고의 퍼센테이지)
      const availableAmount = Math.floor(
        gameState.playerBalance * (percentage / 100)
      );

      // 시장가 또는 지정가 가격 기준으로 계산
      const orderPrice = isMarketOrder
        ? gameState.currentPrice
        : adjustToTickSize(Number(limitOrderPrice) || gameState.currentPrice);

      // 가격이 0이거나 음수이면 계산하지 않음
      if (orderPrice <= 0) {
        return;
      }

      // 매수 수수료만 고려한 수량 계산
      // buyCost = price * qty * (1 + 0.00015)
      // qty = availableAmount / (price * 1.00015)
      const stockQuantityValue = Math.floor(
        availableAmount / (orderPrice * (1 + BROKERAGE_FEE_RATE))
      );

      if (stockQuantityValue > 0) {
        setQuantity(stockQuantityValue.toString());
        if (!isMarketOrder && !limitOrderPrice) {
          setLimitOrderPrice(gameState.currentPrice.toString());
        }
      } else {
        // 계산된 수량이 0이면 수량을 비움
        setQuantity('');
      }
    },
    [
      gameState,
      isMarketOrder,
      limitOrderPrice,
      setQuantity,
      setLimitOrderPrice,
      adjustToTickSize,
    ]
  );

  const calculateSellPercentage = useCallback(
    (percentage: number) => {
      if (!gameState.isGameActive || gameState.stockQuantity <= 0) return;

      const sellQuantity = Math.floor(
        gameState.stockQuantity * (percentage / 100)
      );

      if (sellQuantity > 0) {
        setQuantity(sellQuantity.toString());
        if (!isMarketOrder && !limitOrderPrice) {
          setLimitOrderPrice(gameState.currentPrice.toString());
        }
      }
    },
    [gameState, isMarketOrder, limitOrderPrice, setQuantity, setLimitOrderPrice]
  );

  const processLimitOrders = useCallback(
    (latestCandle: ChartData) => {
      console.log('지정가 주문 처리 시작:', {
        candle: latestCandle,
        candleLow: latestCandle.low,
        candleHigh: latestCandle.high,
      });

      setGameState(prev => {
        console.log('지정가 주문 처리 - 현재 상태:', {
          limitOrdersCount: prev.limitOrders.length,
          limitOrders: prev.limitOrders.map(o => ({
            id: o.id,
            type: o.type,
            price: o.price,
            size: o.size,
            status: o.status,
          })),
          playerBalance: prev.playerBalance,
          stockQuantity: prev.stockQuantity,
        });

        // 체결 조건 확인 및 업데이트
        const updatedOrders: LimitOrder[] = prev.limitOrders.map(order => {
          if (order.status !== 'pending') {
            console.log('이미 처리된 주문:', order.id, order.status);
            return order;
          }

          // 매수 주문 체결 조건: 지정가가 캔들의 고가와 저가 사이에 있을 때
          if (
            order.type === 'buy' &&
            latestCandle.low <= order.price &&
            order.price <= latestCandle.high
          ) {
            console.log('매수 주문 체결:', {
              orderId: order.id,
              orderPrice: order.price,
              candleLow: latestCandle.low,
              candleHigh: latestCandle.high,
            });
            return { ...order, status: 'filled' };
          }

          // 매도 주문 체결 조건: 지정가가 캔들의 고가와 저가 사이에 있을 때
          if (
            order.type === 'sell' &&
            latestCandle.low <= order.price &&
            order.price <= latestCandle.high
          ) {
            if (prev.stockQuantity >= order.size) {
              console.log('매도 주문 체결:', {
                orderId: order.id,
                orderPrice: order.price,
                candleLow: latestCandle.low,
                candleHigh: latestCandle.high,
                stockQuantity: prev.stockQuantity,
              });
              return { ...order, status: 'filled' };
            } else {
              console.warn('매도 주문 체결 불가: 보유 주식 부족', {
                orderId: order.id,
                orderSize: order.size,
                stockQuantity: prev.stockQuantity,
              });
            }
          }

          return order;
        });

        const filledOrders = updatedOrders.filter(
          order => order.status === 'filled'
        );

        console.log('체결된 주문:', filledOrders.length, '개', filledOrders);

        // 체결된 주문이 없으면 상태 변경 없음
        if (filledOrders.length === 0) {
          console.log('체결된 주문 없음, 상태 변경 없음');
          return prev;
        }

        let newBalance = prev.playerBalance;
        let newStockQuantity = prev.stockQuantity;
        let newAveragePrice = prev.averagePrice;
        const newTrades: Trade[] = [];

        // 현재 캔들의 날짜 가져오기 (거래가 발생한 날짜)
        const currentCandle =
          prev.history.length > 0
            ? prev.history[prev.history.length - 1]
            : null;
        const tradeDate = currentCandle
          ? new Date(currentCandle.timestamp)
          : new Date();

        filledOrders.forEach(order => {
          if (order.type === 'buy') {
            // 지정가 매수 체결: 잔고는 이미 createLimitOrder에서 차감되었으므로 그대로 유지
            // 주식 수량만 증가시킴 (총자산 변동 없음)
            // totalCost는 이미 수수료가 포함된 금액 (createLimitOrder에서 차감됨)
            const totalCost = calculateBuyCost(order.price, order.size);
            console.log('매수 주문 체결 처리:', {
              orderId: order.id,
              price: order.price,
              size: order.size,
              totalCost,
              수수료포함: true,
              beforeBalance: newBalance,
              beforeStockQuantity: newStockQuantity,
              beforeAveragePrice: newAveragePrice,
            });

            // 잔고는 이미 차감되었으므로 그대로 유지
            // 주식 수량 증가
            newStockQuantity += order.size;

            // 평균 단가 계산: (기존 총 가치 + 새로 매수한 총 가치) / 총 주식 수량
            const existingTotalValue =
              newAveragePrice * (newStockQuantity - order.size);
            const newTotalValue = existingTotalValue + totalCost;
            newAveragePrice =
              newStockQuantity > 0
                ? Math.floor(newTotalValue / newStockQuantity)
                : order.price;

            console.log('매수 주문 체결 후:', {
              afterBalance: newBalance,
              afterStockQuantity: newStockQuantity,
              afterAveragePrice: newAveragePrice,
              existingTotalValue,
              newTotalValue,
              calculatedAverage: newAveragePrice,
            });

            newTrades.push({
              type: '매수',
              price: order.price,
              size: order.size,
              time: tradeDate,
              profit: 0,
            });
          } else {
            // 매도 주문 체결 (수수료와 거래세 차감)
            const actualRevenue = calculateSellRevenue(order.price, order.size);
            console.log('매도 주문 체결 처리:', {
              orderId: order.id,
              price: order.price,
              size: order.size,
              매도금액: order.price * order.size,
              실제수익: actualRevenue,
              수수료및거래세: order.price * order.size - actualRevenue,
              beforeBalance: newBalance,
              beforeStockQuantity: newStockQuantity,
              beforeAveragePrice: newAveragePrice,
            });

            newBalance += actualRevenue; // 수수료와 거래세 차감된 금액
            newStockQuantity -= order.size;
            // 매도 후에도 주식이 남아있으면 평균 단가는 유지 (LIFO/FIFO 없이 평균 단가 유지)
            newAveragePrice = newStockQuantity > 0 ? newAveragePrice : 0;

            console.log('매도 주문 체결 후:', {
              afterBalance: newBalance,
              afterStockQuantity: newStockQuantity,
              afterAveragePrice: newAveragePrice,
            });

            newTrades.push({
              type: '매도',
              price: order.price,
              size: order.size,
              time: tradeDate,
              profit: 0,
            });
          }
        });

        // 체결된 주문을 limitOrders에서 제거
        const remainingOrders = updatedOrders.filter(
          order => order.status === 'pending'
        );

        console.log('지정가 주문 처리 완료:', {
          filledCount: filledOrders.length,
          remainingCount: remainingOrders.length,
          newBalance,
          newStockQuantity,
          newAveragePrice,
          newTradesCount: newTrades.length,
        });

        if (newTrades.length > 0) {
          setTradeHistory(prevTrades => [...newTrades, ...prevTrades]);
        }

        return {
          ...prev,
          limitOrders: remainingOrders,
          playerBalance: newBalance,
          stockQuantity: newStockQuantity,
          averagePrice: newAveragePrice,
        };
      });
    },
    [setGameState, setTradeHistory]
  );

  return {
    buy,
    sell,
    createLimitOrder,
    cancelLimitOrder,
    calculateBuyPercentage,
    calculateSellPercentage,
    processLimitOrders,
  };
};
